import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { FileManagerUpload } from "@shared/models/file-manager/file-manager-upload.model";
import { FileManagerService } from "@app/core/services/file-manager/file-manager.service";
import { FileUploadStatus } from "@shared/constants/file-manager/file-upload-status";
import { FileUploadOptions, FileUploadType } from "@app/shared/constants/file-manager/file-upload-options";

@Component({
  selector: 'app-file-manager-upload',
  templateUrl: './file-manager-upload.component.html',
  styleUrls: ['./file-manager-upload.component.css']
})
export class FileManagerUploadComponent implements OnInit {
  @Output() onCancel = new EventEmitter<void>();
  @Output() onUploadingInProgress = new EventEmitter<boolean>();
  @Output() onFileProcessed = new EventEmitter<FileManagerUpload>();
  @Input() uploadType: FileUploadType = FileUploadOptions['TEXT'];

  @ViewChild('fileInput') fileInput!: ElementRef;

  uploadedFiles: FileManagerUpload[] = [];
  readonly uploadStatus = FileUploadStatus;
  readonly fileUploadOptions = FileUploadOptions;
  isMaxTextFilesReached: boolean = false;
  isMaxFilesReached: boolean = false;

  currentTab: string = 'text';

  constructor(
    private fileManagerService: FileManagerService,
  ) {
  }

  ngOnInit() {
    this.fileManagerService.uploadedFiles$.subscribe((files: FileManagerUpload[]) => {
      this.uploadedFiles = files;
    });

    this.subscribeToSelectedFileType();
  }

  private subscribeToSelectedFileType() {
    this.fileManagerService.selectedFileType$.subscribe((selectedFileType: FileUploadType) => {
      if( this.currentTab != selectedFileType.fileType) {
        this.isMaxTextFilesReached = false;
        this.isMaxFilesReached = false;
      }
      this.currentTab = selectedFileType.fileType;
    });
  }

  get getErrorMessage(): string {
    switch (this.currentTab) {
      case 'text':
      case 'image':
        return `<b>File limit exceeded:</b>  Only 5 ${this.currentTab === 'text' ? 'files' : 'images'} can be ${this.currentTab === 'text' ? 'processed' : 'uploaded'} at a time.`;
      case 'spreadsheets':
        return "<b>File limit exceeded:</b>  Only 1 file can be uploaded at a time.";
      default:
        return '';
    }
  }

  disableUploader() {
    if (this.uploadType === this.fileUploadOptions['SPREADSHEETS'] &&
      this.fileManagerService.preSelectedFiles$ &&
      this.fileManagerService.preSelectedFiles$.value.length >= 1) {
      return true;
    } else if (this.uploadType === this.fileUploadOptions['IMAGE'] &&
      this.fileManagerService.preSelectedFiles$ &&
      this.fileManagerService.preSelectedFiles$.value.length >= 5) {
      return true;
    }
    return false;
  }

  onNotificationClosed() {
    this.isMaxTextFilesReached = false;
    this.isMaxFilesReached = false;
}

  openFileDialog(): void {
    this.fileInput.nativeElement.click();
  };

  onFileChange(event: any): void {
    const files: FileList = event.target.files;
    this.handleFileUpload(files).then(() => this.fileInput.nativeElement.value = null);
  };

  async handleFileUpload(files: FileList): Promise<void> {
    const validation = this.fileManagerService.validateFileUploadBatch(files, this.uploadType.fileType);
    const filesToUpload: File[] = [];

    if (this.checkIfFileLimitReached(files)) {
      return;
    }

    for (let i = 0; i < files.length; i++) {
      if (!validation.isValid()) {
        if (validation.maxFileSizeExceeded && validation.maxFileSizeExceededIndex?.includes(i)) {
          this.fileManagerService.addUploadedFile({
            file: files.item(i)!,
            status: this.uploadStatus.FILE_SIZE_EXCEEDED,
          });
          continue;
        }
      }

      const supportedFileTypes = this.fileManagerService.getFilesByType(this.uploadType.fileType) || [];
      if (!supportedFileTypes.includes(files.item(i)!.type)) {
        continue;
      }

      const file = files.item(i)!;
      if (!this.fileManagerService.validFileName(file.name)) {
        continue;
      }

      if (await this.fileManagerService.isFilePasswordProtected(file)) {
        this.fileManagerService.addUploadedFile({
          file: file,
          status: this.uploadStatus.PASSWORD_PROTECTED,
        })
        continue;
      }

      const isAlreadyInUploadList = this.uploadedFiles.some(
        (uploadedFile) => uploadedFile.file.name === file.name
      );

      if (isAlreadyInUploadList) continue;

      filesToUpload.push(file);
    }

    if (filesToUpload.length > 0) {
      this.fileManagerService.uploadFiles(filesToUpload, this.uploadType).forEach(
        (fileUpload) => {
          const upload = this.fileManagerService.getFileUploadStatus(fileUpload);
          this.fileManagerService.addUploadedFile(upload);
        }
      );
    }
  };

  checkIfFileLimitReached(files: FileList): boolean {
    const maxFiles = this.uploadType.maxFiles;
    let isMaxFilesReached: boolean;
  
    if (this.uploadType === this.fileUploadOptions['TEXT']) {
      isMaxFilesReached = files.length > maxFiles;
      this.isMaxTextFilesReached = isMaxFilesReached;
    } else {
      const preSelectedFilesCount = this.fileManagerService.preSelectedFiles$.value.length;
      isMaxFilesReached = preSelectedFilesCount + files.length > maxFiles;
      this.isMaxFilesReached = isMaxFilesReached;
    }
  
    return isMaxFilesReached;
  } 

  get wrapperClasses(): { [key: string]: boolean } {
    return {
      'has-files': this.uploadedFiles.length > 0,
      'single-file': this.uploadedFiles.length === 1
    }
  };
}
